import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useMobile } from '../components/Layout';
import { useTheme } from '../ThemeContext';
import { Download, Upload, ChevronLeft, CheckCircle, FileText, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
const API = axios.create({ baseURL: API_URL });
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const StatusPill = ({ children, tone = 'neutral', theme }) => {
  const styles = {
    success: { bg: '#F8FAFC', color: '#166534', border: '#DCFCE7' },
    warning: { bg: '#FFFBEB', color: '#92400E', border: '#FDE68A' },
    danger: { bg: '#FEF2F2', color: '#991B1B', border: '#FECACA' },
    neutral: { bg: theme.bgInput, color: theme.textSecondary, border: theme.border },
  };
  const s = styles[tone] || styles.neutral;

  return (
    <span style={{
      background: s.bg,
      color: s.color,
      border: `1px solid ${s.border}`,
      padding: '3px 8px',
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  );
};

const Migration = () => {
  const navigate = useNavigate();
  const isMobile = useMobile();
  const { theme } = useTheme();
  const fileRef = useRef(null);
  const eventSourceRef = useRef(null);

  const [step, setStep] = useState('upload');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [batchId, setBatchId] = useState(null);
  const [progress, setProgress] = useState({ processed: 0, total: 0, imported: 0, skipped: 0, flagged: 0 });
  const [log, setLog] = useState([]);
  const [result, setResult] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [showAllBatches, setShowAllBatches] = useState(false);

  useEffect(() => { loadBatches(); }, []);

  const loadBatches = async () => {
    try {
      const res = await API.get('/migration/batches');
      setBatches(res.data.batches || []);
    } catch {
      setBatches([]);
    } finally {
      setLoadingBatches(false);
    }
  };

  const downloadTemplate = () => {
    const token = localStorage.getItem('token');
    window.open(`${API_URL}/migration/template?token=${token}`, '_blank');
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) {
      setFile(f);
      setPreview(null);
    }
  };

  const handlePreview = async () => {
    if (!file) return;
    setPreviewing(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await API.post('/migration/preview', formData);
      setPreview(res.data);
      setStep('preview');
    } catch (err) {
      alert(err.response?.data?.error || 'Preview failed.');
    } finally {
      setPreviewing(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setStep('importing');
    setLog([]);
    setProgress({ processed: 0, total: preview?.total_rows || 0, imported: 0, skipped: 0, flagged: 0 });

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await API.post('/migration/import', formData);
      const newBatchId = res.data.batch_id;
      setBatchId(newBatchId);

      const token = localStorage.getItem('token');
      const es = new EventSource(`${API_URL}/migration/progress/${newBatchId}?token=${token}`);
      eventSourceRef.current = es;

      es.onmessage = (e) => {
        const data = JSON.parse(e.data);

        if (data.type === 'start') {
          setProgress(p => ({ ...p, total: data.total }));
        }

        if (data.type === 'progress') {
          setProgress(p => ({
            ...p,
            processed: data.processed,
            imported: p.imported + (data.status === 'imported' || data.status === 'flagged' ? 1 : 0),
            skipped: p.skipped + (data.status === 'skipped' ? 1 : 0),
            flagged: p.flagged + (data.status === 'flagged' ? 1 : 0),
          }));

          setLog(prev => [{ row: data.row, status: data.status, pseudo_id: data.pseudo_id }, ...prev].slice(0, 50));
        }

        if (data.type === 'complete') {
          setResult(data);
          setStep('done');
          es.close();
          loadBatches();
        }

        if (data.type === 'error') {
          alert(`Migration failed: ${data.message}`);
          setStep('upload');
          es.close();
        }
      };

      es.onerror = () => es.close();
    } catch (err) {
      alert(err.response?.data?.error || 'Import failed.');
      setStep('preview');
    }
  };

  const downloadReport = (bId) => {
    const token = localStorage.getItem('token');
    window.open(`${API_URL}/migration/report/${bId}?token=${token}`, '_blank');
  };

  const reset = () => {
    setStep('upload');
    setFile(null);
    setPreview(null);
    setBatchId(null);
    setProgress({ processed: 0, total: 0, imported: 0, skipped: 0, flagged: 0 });
    setLog([]);
    setResult(null);

    if (fileRef.current) fileRef.current.value = '';
    if (eventSourceRef.current) eventSourceRef.current.close();
  };

  const pct = progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0;

  const card = {
    background: theme.bgCard,
    borderRadius: 10,
    border: `1px solid ${theme.border}`,
    boxShadow: theme.shadowSm,
    padding: isMobile ? 16 : 20,
    marginBottom: 16,
  };

  const primaryBtn = {
    padding: '9px 15px',
    borderRadius: 8,
    border: '1px solid #2563EB',
    background: '#2563EB',
    color: 'white',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
  };

  const secondaryBtn = {
    padding: '9px 15px',
    borderRadius: 8,
    border: `1px solid ${theme.border}`,
    background: theme.bgInput,
    color: theme.textSecondary,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
  };

  const disabledBtn = {
    ...primaryBtn,
    background: theme.bgInput,
    border: `1px solid ${theme.border}`,
    color: theme.textTertiary,
    cursor: 'not-allowed',
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <button onClick={() => navigate('/')} style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: theme.textSecondary,
          fontSize: 12,
          fontWeight: 600,
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          marginBottom: 12,
        }}>
          <ChevronLeft size={14} /> Dashboard
        </button>

        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: theme.textPrimary }}>
          Investor Migration
        </h1>
        <p style={{ margin: '5px 0 0', fontSize: 13, color: theme.textSecondary }}>
          Bulk import legacy investor records with validation, audit logging, and migration reporting.
        </p>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) 340px',
        gap: 20,
      }}>
        <div>
          {(step === 'upload' || step === 'preview') && (
            <div style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 18 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: theme.textPrimary }}>
                    Upload file
                  </h3>
                  <p style={{ margin: '4px 0 0', fontSize: 13, color: theme.textSecondary }}>
                    Use the RegShield template to import CSV or Excel data.
                  </p>
                </div>
                <StatusPill theme={theme}>Step 1</StatusPill>
              </div>

              <button onClick={downloadTemplate} style={{ ...secondaryBtn, marginBottom: 18 }}>
                <Download size={14} /> Download Template
              </button>

              <div style={{
                border: `1px dashed ${theme.border}`,
                borderRadius: 10,
                padding: isMobile ? 20 : 28,
                textAlign: 'center',
                background: theme.bgInput,
                marginBottom: 16,
              }}>
                <Upload size={28} color={theme.textTertiary} style={{ marginBottom: 10 }} />
                <p style={{ margin: '0 0 12px', fontSize: 13, color: theme.textSecondary }}>
                  Select your completed migration file
                </p>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFileChange} style={{ fontSize: 13, color: theme.textSecondary }} />
                {file && (
                  <p style={{ margin: '10px 0 0', fontSize: 12, color: theme.textPrimary, fontWeight: 600 }}>
                    {file.name}
                  </p>
                )}
              </div>

              <button onClick={handlePreview} disabled={!file || previewing} style={{
                ...(!file || previewing ? disabledBtn : primaryBtn),
                width: '100%',
              }}>
                {previewing ? 'Analysing file...' : 'Preview and Validate'}
              </button>
            </div>
          )}

          {step === 'preview' && preview && (
            <div style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 18 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: theme.textPrimary }}>
                    Preview and confirm
                  </h3>
                  <p style={{ margin: '4px 0 0', fontSize: 13, color: theme.textSecondary }}>
                    Showing first {preview.preview?.length} of {preview.total_rows} rows.
                  </p>
                </div>
                <StatusPill theme={theme}>Step 2</StatusPill>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                border: `1px solid ${theme.border}`,
                borderRadius: 10,
                overflow: 'hidden',
                marginBottom: 16,
              }}>
                {[
                  ['Total Rows', preview.total_rows, 'neutral'],
                  ['Errors', preview.error_count || 0, 'danger'],
                  ['Warnings', preview.warning_count || 0, 'warning'],
                ].map(([label, value, tone], idx) => (
                  <div key={label} style={{
                    padding: '14px 12px',
                    borderRight: idx < 2 ? `1px solid ${theme.border}` : 'none',
                    background: theme.bgInput,
                  }}>
                    <p style={{ margin: 0, fontSize: 12, color: theme.textTertiary, fontWeight: 600 }}>{label}</p>
                    <p style={{
                      margin: '6px 0 0',
                      fontSize: 23,
                      fontWeight: 700,
                      color: tone === 'danger' ? '#991B1B' : tone === 'warning' ? '#92400E' : theme.textPrimary,
                      lineHeight: 1,
                    }}>
                      {value}
                    </p>
                  </div>
                ))}
              </div>

              <div style={{
                background: preview.template_match?.is_template ? '#F8FAFC' : '#FFFBEB',
                border: `1px solid ${preview.template_match?.is_template ? theme.border : '#FDE68A'}`,
                borderRadius: 8,
                padding: '10px 13px',
                marginBottom: 16,
                fontSize: 12,
                color: preview.template_match?.is_template ? theme.textSecondary : '#92400E',
                fontWeight: 600,
                display: 'flex',
                gap: 8,
                alignItems: 'center',
              }}>
                {preview.template_match?.is_template ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
                {preview.template_match?.is_template
                  ? 'File matches the RegShield template format.'
                  : `${preview.template_match?.unmatched?.length} unrecognised column(s): ${preview.template_match?.unmatched?.join(', ')}`}
              </div>

              <div style={{ maxHeight: 400, overflowY: 'auto', marginBottom: 16, display: 'grid', gap: 8 }}>
                {preview.preview?.map(row => {
                  const hasError = row.issues?.some(i => i.severity === 'ERROR');
                  const hasWarning = row.issues?.some(i => i.severity === 'WARNING');

                  return (
                    <div key={row.row_number} style={{
                      borderRadius: 8,
                      padding: '12px 14px',
                      border: `1px solid ${hasError ? '#FECACA' : hasWarning ? '#FDE68A' : theme.border}`,
                      background: hasError ? '#FEF2F2' : hasWarning ? '#FFFBEB' : theme.bgInput,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: theme.textTertiary }}>Row {row.row_number}</span>
                        <StatusPill theme={theme} tone={hasError ? 'danger' : hasWarning ? 'warning' : 'success'}>
                          {hasError ? 'Error' : hasWarning ? 'Warning' : 'Ready'}
                        </StatusPill>
                      </div>

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                        gap: '4px 12px',
                        marginBottom: row.issues?.length > 0 ? 8 : 0,
                      }}>
                        {['full_name', 'email', 'nationality', 'fund_isin', 'investor_type', 'status'].map(col => (
                          row.data[col] ? (
                            <div key={col}>
                              <span style={{ fontSize: 10, fontWeight: 600, color: theme.textTertiary }}>{col.replace(/_/g, ' ')}: </span>
                              <span style={{ fontSize: 12, color: theme.textPrimary }}>{String(row.data[col])}</span>
                            </div>
                          ) : null
                        ))}
                      </div>

                      {row.issues?.length > 0 && (
                        <div style={{ display: 'grid', gap: 3 }}>
                          {row.issues.map((issue, i) => (
                            <p key={i} style={{ margin: 0, fontSize: 11, fontWeight: 500, color: issue.severity === 'ERROR' ? '#991B1B' : '#92400E' }}>
                              [{issue.field}] {issue.message}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={reset} style={{ ...secondaryBtn, flex: 1 }}>
                  Different File
                </button>
                <button onClick={handleImport} style={{ ...primaryBtn, flex: 2 }}>
                  Import {preview.total_rows} investors
                </button>
              </div>
            </div>
          )}

          {step === 'importing' && (
            <div style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 18 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: theme.textPrimary }}>
                    Importing records
                  </h3>
                  <p style={{ margin: '4px 0 0', fontSize: 13, color: theme.textSecondary }}>
                    Processing migration batch in real time.
                  </p>
                </div>
                <StatusPill theme={theme}>{pct}%</StatusPill>
              </div>

              <div style={{
                background: theme.bgInput,
                borderRadius: 999,
                height: 8,
                marginBottom: 16,
                overflow: 'hidden',
                border: `1px solid ${theme.border}`,
              }}>
                <div style={{
                  height: '100%',
                  background: '#2563EB',
                  width: `${pct}%`,
                  transition: 'width 0.3s',
                }} />
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                border: `1px solid ${theme.border}`,
                borderRadius: 10,
                overflow: 'hidden',
                marginBottom: 16,
              }}>
                {[
                  ['Processed', progress.processed, 'neutral'],
                  ['Imported', progress.imported, 'success'],
                  ['Skipped', progress.skipped, 'danger'],
                  ['Flagged', progress.flagged, 'warning'],
                ].map(([label, value, tone], idx) => (
                  <div key={label} style={{
                    padding: '14px 10px',
                    background: theme.bgInput,
                    borderRight: idx < 3 ? `1px solid ${theme.border}` : 'none',
                    textAlign: 'center',
                  }}>
                    <p style={{
                      margin: 0,
                      fontSize: 22,
                      fontWeight: 700,
                      color: tone === 'danger' ? '#991B1B' : tone === 'warning' ? '#92400E' : theme.textPrimary,
                    }}>
                      {value}
                    </p>
                    <p style={{ margin: '3px 0 0', fontSize: 10, color: theme.textTertiary }}>{label}</p>
                  </div>
                ))}
              </div>

              <div style={{
                background: theme.bgInput,
                border: `1px solid ${theme.border}`,
                borderRadius: 8,
                padding: 12,
                maxHeight: 200,
                overflowY: 'auto',
                fontFamily: 'monospace',
                fontSize: 11,
              }}>
                {log.length === 0 ? (
                  <p style={{ color: theme.textTertiary, margin: 0 }}>Waiting for migration events...</p>
                ) : log.map((entry, i) => (
                  <p key={i} style={{
                    margin: '3px 0',
                    color: entry.status === 'flagged' ? '#92400E' : entry.status === 'skipped' ? '#991B1B' : theme.textSecondary,
                  }}>
                    Row {entry.row} - {entry.status}{entry.pseudo_id ? ` - ${entry.pseudo_id}` : ''}
                  </p>
                ))}
              </div>
            </div>
          )}

          {step === 'done' && result && (
            <div style={card}>
              <div style={{ textAlign: 'center', marginBottom: 22 }}>
                <CheckCircle size={42} color="#166534" style={{ marginBottom: 12 }} />
                <h3 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 700, color: theme.textPrimary }}>
                  Migration complete
                </h3>
                <p style={{ margin: 0, fontSize: 12, color: theme.textTertiary }}>Batch ID: {batchId}</p>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                border: `1px solid ${theme.border}`,
                borderRadius: 10,
                overflow: 'hidden',
                marginBottom: 16,
              }}>
                {[
                  ['Imported', result.imported, 'success'],
                  ['Skipped', result.skipped, 'danger'],
                  ['Flagged', result.flagged, 'warning'],
                ].map(([label, value, tone], idx) => (
                  <div key={label} style={{
                    padding: 16,
                    background: theme.bgInput,
                    borderRight: idx < 2 ? `1px solid ${theme.border}` : 'none',
                    textAlign: 'center',
                  }}>
                    <p style={{
                      margin: 0,
                      fontSize: 26,
                      fontWeight: 700,
                      color: tone === 'danger' ? '#991B1B' : tone === 'warning' ? '#92400E' : theme.textPrimary,
                    }}>
                      {value}
                    </p>
                    <p style={{ margin: '4px 0 0', fontSize: 11, color: theme.textTertiary }}>{label}</p>
                  </div>
                ))}
              </div>

              <div style={{
                background: '#F8FAFC',
                border: `1px solid ${theme.border}`,
                borderRadius: 8,
                padding: '12px 14px',
                marginBottom: 16,
                fontSize: 12,
                color: theme.textSecondary,
                fontWeight: 600,
              }}>
                Migration event recorded in the immutable audit log.
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => downloadReport(batchId)} style={{ ...primaryBtn, flex: 1 }}>
                  <Download size={14} /> PDF Report
                </button>
                <button onClick={reset} style={{ ...secondaryBtn, flex: 1 }}>
                  New Migration
                </button>
              </div>
            </div>
          )}
        </div>

        <div>
          <div style={{ ...card, marginBottom: 0 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700, color: theme.textPrimary }}>
              Previous Migrations
            </h3>

            {loadingBatches ? (
              <p style={{ fontSize: 13, color: theme.textSecondary }}>Loading...</p>
            ) : batches.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <FileText size={28} color={theme.textTertiary} style={{ marginBottom: 8 }} />
                <p style={{ fontSize: 13, color: theme.textSecondary, margin: 0 }}>No migrations yet.</p>
              </div>
            ) : batches.slice(0, showAllBatches ? batches.length : 5).map(batch => (
              <div key={batch.id} style={{
                padding: 12,
                borderRadius: 8,
                marginBottom: 8,
                border: `1px solid ${theme.border}`,
                background: theme.bgInput,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 7, gap: 8 }}>
                  <p style={{
                    margin: 0,
                    fontSize: 12,
                    fontWeight: 600,
                    color: theme.textPrimary,
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {batch.source_filename}
                  </p>
                  <StatusPill
                    theme={theme}
                    tone={batch.status === 'COMPLETED' ? 'success' : batch.status === 'FAILED' ? 'danger' : 'warning'}
                  >
                    {batch.status}
                  </StatusPill>
                </div>

                <p style={{ margin: '0 0 8px', fontSize: 11, color: theme.textTertiary }}>
                  {new Date(batch.started_at).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>

                <div style={{ display: 'flex', gap: 10, fontSize: 11, marginBottom: 8 }}>
                  <span style={{ color: theme.textSecondary, fontWeight: 600 }}>Imported {batch.imported_count}</span>
                  <span style={{ color: '#991B1B', fontWeight: 600 }}>Skipped {batch.skipped_count}</span>
                  <span style={{ color: '#92400E', fontWeight: 600 }}>Flagged {batch.flagged_count}</span>
                </div>

                {batch.status === 'COMPLETED' && (
                  <button onClick={() => downloadReport(batch.id)} style={{ ...secondaryBtn, width: '100%', padding: '7px 10px', fontSize: 12 }}>
                    <Download size={12} /> PDF Report
                  </button>
                )}
              </div>
            ))}

            {!loadingBatches && batches.length > 5 && (
              <button onClick={() => setShowAllBatches(v => !v)} style={{
                width: '100%',
                padding: '8px',
                borderRadius: 8,
                background: theme.bgInput,
                border: `1px solid ${theme.border}`,
                fontSize: 12,
                color: theme.textSecondary,
                cursor: 'pointer',
                marginTop: 4,
              }}>
                {showAllBatches ? 'Show less' : `Show all (${batches.length})`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Migration;
